import { supportsPaidExtension } from '@/module/payment/shared/constants/paymentIntent';
import { type UserInfo } from '@/types';

import { type PaidPlanType } from './upsellingPolicy';

/**
 * 업셀링 모달이 화면을 가르는 축.
 *
 * 등급(`planType`) 하나로는 개인 Basic · 어드민 Basic · 단체 Basic · 이용권 Basic 을 구분할 수 없어서,
 * 같은 사용자가 진입점마다 다른 CTA 를 받는 문제가 있었다
 * (`docs/basic-to-pro-upselling-routing-audit.md`). 소속·과금 유형까지 반영해 판정한다.
 *
 * `unknown` 이 1급 상태인 것이 핵심이다. "모른다"를 무료나 개인 구매자로 뭉개면
 * 결제 중인 사용자에게 신규 구매 CTA 가 뜬다.
 */
export type UpsellingPlanState =
  | { kind: 'anonymous' }
  /** 유료 등급은 아는데 소속·과금 유형을 아직 모른다. 서버 필드 반영 전 전체가 여기 해당한다. */
  | { kind: 'unknown'; tier: PaidPlanType }
  | { kind: 'free' }
  /** 개인 직접구매 플랜. 어드민 주문도 연장 가능하지만 업그레이드는 셀프서브 주문만 가능하다. */
  | {
      kind: 'individual';
      tier: PaidPlanType;
      selfServicePurchase: boolean;
    }
  /** 개인 워크스페이스의 비직접 구매 플랜. 미래 구매만 허용한다. */
  | { kind: 'purchase-only' }
  /** 단체 워크스페이스 소유자·멤버. 개인 결제 흐름을 태우지 않는다. */
  | { kind: 'restricted' };

/**
 * `useUser()` 가 주는 값만으로 상태를 판정한다.
 *
 * 관리 API(`GET /quiz-plans/management`)를 부르지 않는 이유는 모달 진입점 12곳 중
 * 11곳이 그 캐시 밖이라 열 때마다 로딩이 끼기 때문이다. 대신 상시 갱신되는
 * `/quiz-plans/options` 에 소속·발급 경로·주문 주체를 요청해 두었다
 * (`docs/upselling-plan-state-api-handoff.md`).
 *
 * 최종 결제 가능 여부는 Checkout 과 서버가 다시 판정한다. 여기서는 *사실*만 읽고
 * 가능 여부를 재계산하지 않는다.
 */
export const resolveUpsellingPlanState = (
  user: UserInfo | null | undefined,
): UpsellingPlanState => {
  if (!user) return { kind: 'anonymous' };

  const tier: PaidPlanType | null =
    user.planType === 'BASIC' || user.planType === 'PRO' ? user.planType : null;

  // 등급이 남아 있어도 활성이 아니면 이용 중이 아니다. planStatus 를 빼면 만료된 Basic
  // 사용자에게 "이용 기간 연장하기"가 뜬다.
  if (!tier || user.planStatus !== 'ACTIVE') return { kind: 'free' };

  // 단체 소유자와 멤버 모두 개인 플랜 구매 대상이 아니다.
  if (user.workspace.scope === 'ORGANIZATION') return { kind: 'restricted' };

  // 여기까지 왔는데 소속·발급 경로 중 하나라도 비어 있으면 개인 구매자라고 단정할 수 없다.
  // 서버 필드가 반영되기 전까지는 모든 유료 사용자가 이 경로로 떨어지고,
  // 정책 레이어가 기존(등급 기반) 동작을 그대로 유지한다.
  if (!user.workspace.scope || !user.planSourceType) {
    return { kind: 'unknown', tier };
  }

  if (user.planSourceType === 'VOUCHER') {
    return { kind: 'purchase-only' };
  }

  if (!supportsPaidExtension(user.planSourceType)) {
    return { kind: 'unknown', tier };
  }
  if (
    user.planSourceType === 'DIRECT_PURCHASE' &&
    user.selfServicePurchase === undefined
  ) {
    return { kind: 'unknown', tier };
  }

  return {
    kind: 'individual',
    tier,
    selfServicePurchase:
      user.planSourceType === 'DIRECT_PURCHASE' &&
      user.selfServicePurchase === true,
  };
};
